import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface ContractorProfile {
  id: string;
  user_id: string;
  company_name: string;
  contact_name: string;
  phone: string;
  approved: boolean;
  created_at: string;
}

export const useContractorAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ContractorProfile | null>(null);
  const [isContractor, setIsContractor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const checkRolesAndProfile = async (userId: string) => {
      try {
        // Check all roles at once
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);

        if (!mounted) return;

        const roleSet = new Set(roles?.map((r: any) => r.role) || []);
        const hasContractor = roleSet.has("contractor");
        const hasAdmin = roleSet.has("admin");

        setIsContractor(hasContractor || hasAdmin);
        setIsAdmin(hasAdmin);

        if (hasContractor || hasAdmin) {
          // Try to fetch existing contractor profile
          const { data: profileData } = await supabase
            .from("contractor_profiles")
            .select("*")
            .eq("user_id", userId)
            .maybeSingle();

          if (mounted && profileData) {
            setProfile(profileData as ContractorProfile);
          } else if (mounted && hasAdmin && !profileData) {
            // Auto-create a contractor profile for admins
            const { data: newProfile } = await supabase
              .from("contractor_profiles")
              .insert({
                user_id: userId,
                company_name: "Admin",
                contact_name: "",
                phone: "",
                approved: true,
              })
              .select()
              .single();

            if (mounted && newProfile) {
              setProfile(newProfile as ContractorProfile);
            }
          }
        }
      } catch {
        if (mounted) {
          setIsContractor(false);
          setIsAdmin(false);
          setProfile(null);
        }
      }
    };

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        await checkRolesAndProfile(currentUser.id);
      }
      if (mounted) setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) {
          setTimeout(() => {
            if (mounted) {
              checkRolesAndProfile(currentUser.id).then(() => {
                if (mounted) setLoading(false);
              });
            }
          }, 0);
        } else {
          setIsContractor(false);
          setIsAdmin(false);
          setProfile(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signUp = async (data: {
    email: string;
    password: string;
    company_name: string;
    contact_name: string;
    phone: string;
  }) => {
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contractor-signup`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify(data),
      }
    );
    return res.json();
  };

  return { user, profile, isContractor, isAdmin, loading, signIn, signOut, signUp };
};
